import React, { useEffect, useState } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { Bar, Line, Doughnut, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  RadialLinearScale,
  PointElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { todayDR } from '../utils/dates';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, RadialLinearScale, Filler, Tooltip, Legend);

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTH_NAMES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const PERIOD_ROWS = [['Hoy', 'day'], ['Esta semana', 'week'], ['Este mes', 'month']];
const PERIOD_ROWS_YEAR = [...PERIOD_ROWS, ['Este año', 'year']];
const HEADING_FONT = "'Space Grotesk', sans-serif";

// Today / this week / this month (and, for cards that pass PERIOD_ROWS_YEAR, this year too) for one figure, "hoy"
// biggest. `values` = { day, week, month, year? }.
const PeriodRows = ({ values, format, color, rows = PERIOD_ROWS }) => (
  <div className="mt-2">
    {rows.map(([label, key], i) => (
      <div key={key} className="d-flex justify-content-between align-items-baseline" style={{ marginTop: i ? 4 : 0 }}>
        <span className="text-muted small">{label}</span>
        <span style={{ fontFamily: HEADING_FONT, fontWeight: i === 0 ? 600 : 500, fontSize: i === 0 ? '1.35rem' : '1.05rem', color: i === 0 ? color : undefined }}>
          {format(values?.[key])}
        </span>
      </div>
    ))}
  </div>
);

// This calendar month vs. the same month last year, stock-ticker style (an up/down caret + percentage, colored
// green/red). Spells out which two figures are being compared — "Este mes" ties it back to the row of the same
// name above, and both amounts are shown on their own line — since a bare "Comparado con <mes>" plus a lone
// percentage left it unclear what was actually being measured against what.
const YoyComparison = ({ current, previous, month, year, format = String }) => {
  const label = `${MONTH_NAMES[((month || 1) - 1 + 12) % 12]} ${year}`;
  const hasPrevious = previous > 0;
  const pct = hasPrevious ? ((current - previous) / previous) * 100 : null;
  const isUp = pct !== null && pct > 0.05;
  const isDown = pct !== null && pct < -0.05;
  const color = isUp ? 'var(--mm-green)' : isDown ? 'var(--mm-red)' : 'var(--mm-muted)';
  const icon = isUp ? 'bi-caret-up-fill' : isDown ? 'bi-caret-down-fill' : null;
  const text = !hasPrevious ? (current > 0 ? 'Nuevo' : 'Sin datos') : `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;

  return (
    <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--mm-line)' }}>
      <div className="d-flex justify-content-between align-items-center">
        <span className="text-muted small">Este mes vs. {label}</span>
        <span className="d-flex align-items-center" style={{ gap: 4, color, fontWeight: 600, fontSize: '0.9rem' }}>
          {icon && <i className={`bi ${icon}`} />}
          {text}
        </span>
      </div>
      {hasPrevious && (
        <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>
          {format(current)} vs. {format(previous)}
        </div>
      )}
    </div>
  );
};

// A dashboard card: a title and its figure(s), optionally a link to the page behind it.
const StatCard = ({ title, to, navigate, children }) => (
  <Card className={`shadow-sm p-3 dashboard-card${to ? ' dashboard-card-link' : ''}`} style={{ justifyContent: 'flex-start' }} onClick={to ? () => navigate(to) : undefined}>
    <div className="text-muted small">{title}</div>
    {children}
  </Card>
);

// Slice colors for the "Gastos del mes" pie (the theme's accents first, the folded "Demás categorías" in grey).
// The tanks-by-document card: slice order, label, color, and where a click goes.
const DOC_KINDS = [
  { key: 'factura', label: 'Con factura', color: '#4ADE80', to: () => `/invoices?despachos=${todayDR().slice(0, 7)}` },
  { key: 'acuerdo', label: 'Acuerdo', color: '#60A5FA', to: () => '/conduces?tab=acuerdos' },
  { key: 'conduce', label: 'Solo conduce', color: '#F5B700', to: () => '/conduces?tab=sin_facturar' },
  { key: 'none', label: 'Sin documento', color: '#F87171', to: () => `/despachos?mes=${todayDR().slice(0, 7)}&tipo=none` },
];
const EXPENSE_COLORS = ['#12B8B2', '#F5B700', '#4ADE80', '#F87171', '#60A5FA', '#C084FC', '#9DB3C2'];

// Admins: this month's expenses as a pie by category, with the month's total at the bottom. Clicking a slice (or its
// legend entry) opens Gastos filtered to that category and month; anywhere else on the card opens the whole month.
const ExpensesCard = ({ gastos, navigate }) => {
  const slices = gastos?.byCategory || [];
  const total = gastos?.total || 0;
  const monthLink = (category) => {
    const params = new URLSearchParams({ desde: gastos?.from || '', hasta: gastos?.to || '' });
    if (category) params.set('categoria', category);
    return `/expenses?${params}`;
  };
  const openSlice = (index) => {
    const slice = slices[index];
    navigate(monthLink(slice && !slice.rest ? slice.category : ''));
  };
  const chart = {
    labels: slices.map(s => s.category),
    datasets: [{
      data: slices.map(s => s.total),
      backgroundColor: slices.map((s, i) => (s.rest ? EXPENSE_COLORS[6] : EXPENSE_COLORS[i % 6])),
      borderColor: '#142534',
      borderWidth: 2,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%',
    onClick: (evt, elements) => openSlice(elements.length ? elements[0].index : -1),
    onHover: (evt, elements) => { evt.native.target.style.cursor = elements.length ? 'pointer' : 'default'; },
    plugins: {
      // Our own list beside the donut (below): Chart.js' legend wraps into a second column when the card is short.
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => `${c.label}: ${money(c.parsed)} (${total ? Math.round((c.parsed / total) * 100) : 0}%)` } },
    },
  };
  return (
    <StatCard title="Gastos del mes por categoría" to={monthLink('')} navigate={navigate}>
      {slices.length === 0 ? (
        <div className="text-center text-muted small flex-grow-1 d-flex align-items-center justify-content-center">Sin gastos registrados este mes.</div>
      ) : (
        // The chart handles its own clicks (a slice → that category); stop them reaching the card's link.
        <div className="mt-2 d-flex align-items-center gap-3" style={{ height: 140 }} onClick={(e) => e.stopPropagation()}>
          <div style={{ width: 140, height: 140, flexShrink: 0 }}><Doughnut data={chart} options={options} /></div>
          <ul className="list-unstyled mb-0 small flex-grow-1" style={{ minWidth: 0, color: '#9DB3C2' }}>
            {slices.map((s, i) => (
              <li key={s.category} className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', lineHeight: 1.6 }} onClick={() => openSlice(i)}>
                <span style={{ width: 10, height: 10, flexShrink: 0, background: chart.datasets[0].backgroundColor[i] }} />
                <span className="text-truncate" title={s.category}>{s.category}</span>
                <span className="ms-auto text-nowrap">{money(s.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-auto pt-2 d-flex justify-content-between align-items-baseline" style={{ borderTop: '1px solid var(--mm-line)' }}>
        <span className="text-muted small">Gastos del mes</span>
        <span style={{ fontFamily: HEADING_FONT, fontWeight: 600, fontSize: '1.35rem', color: 'var(--mm-red)' }}>{money(total)}</span>
      </div>
    </StatCard>
  );
};

const SalesDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/dashboard')
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Error fetching dashboard snapshot:', err);
        setError('No se pudo cargar el panel de ventas.');
      });
  }, []);

  if (error) {
    return <div className="container mt-5"><div className="alert alert-danger">{error}</div></div>;
  }

  if (!data) {
    return <div className="container mt-5 text-muted">Cargando panel de ventas...</div>;
  }

  const dailyChart = {
    labels: data.dailySalesVsQuotes.map(d => d.day),
    datasets: [
      { label: 'Ventas', data: data.dailySalesVsQuotes.map(d => d.ventas), backgroundColor: 'rgba(18,184,178,0.6)', borderRadius: 4 },
      { label: 'Cotizaciones', data: data.dailySalesVsQuotes.map(d => d.cotizaciones), backgroundColor: 'rgba(74,222,128,0.6)', borderRadius: 4 },
    ],
  };

  // Sales staff see two charts side by side (sales vs quotes, and income receipts) sized to fit the
  // screen without scrolling; admins keep the original layout with the year-over-year chart.
  const incomeChart = {
    labels: (data.dailyIncome || []).map(d => d.day),
    datasets: [{ label: 'Recibos de ingresos', data: (data.dailyIncome || []).map(d => d.recibos), backgroundColor: 'rgba(74,222,128,0.65)', borderRadius: 4 }],
  };
  const fitToScreen = (chart) => (
    <div style={{ height: 'clamp(260px, 42vh, 420px)' }}>{chart}</div>
  );
  const sideBySide = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } };

  // Company-wide totals, the year-over-year chart and the rankings only come from the server for admins.
  const showCompany = Array.isArray(data.monthlyComparison);

  // Admins: cylinders sold this month vs last month, as running totals day by day (backend cylindersPace) — volume at a
  // glance, which the product mix can't show while one product dominates.
  const pace = data.cylindersPace;
  const paceFromManifold = pace?.source === 'manifold'; // despachos per Manifold Manager; invoiced cylinders if it didn't answer
  const paceDays = pace ? Math.max(pace.daysInMonth, pace.lastMonth.length) : 0;
  const paceChart = pace && {
    labels: Array.from({ length: paceDays }, (_, i) => i + 1),
    datasets: [
      { label: 'Este mes', data: pace.thisMonth, borderColor: '#12B8B2', backgroundColor: 'rgba(18,184,178,0.15)', fill: true, tension: 0.25, pointRadius: 0, borderWidth: 2 },
      { label: 'Mes pasado', data: pace.lastMonth, borderColor: 'rgba(157,179,194,0.8)', borderDash: [5, 4], fill: false, tension: 0.25, pointRadius: 0, borderWidth: 2 },
    ],
  };
  const paceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { callbacks: { title: (items) => `Día ${items[0].label}`, label: (c) => `${c.dataset.label}: ${c.parsed.y.toLocaleString('es-DO')} cilindros` } },
    },
    scales: { y: { beginAtZero: true }, x: { ticks: { maxTicksLimit: 10 } } },
  };

  // Admins: this month's despachos (Manifold Manager) by the paperwork behind them (backend services/dispatchedTanks.js
  // byDocument): invoiced, on a conduce never invoiced, or with no Ventas document. null = Manifold didn't answer.
  const byDocument = data.dispatchedByDocument;
  const docSlices = byDocument ? DOC_KINDS.map(k => ({ ...k, ...(byDocument[k.key] || { despachos: 0, tanks: 0 }) })) : [];
  const docTanks = docSlices.reduce((sum, d) => sum + d.tanks, 0);
  // A radar ("web") like the dashboard always had. The spokes are on a COMPRESSED scale (log10 of tanks + 1), or the
  // invoiced share (~90%) would leave the other two points at the center; the real numbers are on the spoke labels.
  const pct = (n) => (docTanks ? Math.round((n / docTanks) * 100) : 0);
  const docChart = {
    labels: docSlices.map(d => d.label),
    // The web is the app's teal; only the points carry each kind's color (the numbers are in the legend under the chart).
    datasets: [{
      label: 'Tanques', data: docSlices.map(d => Math.log10(d.tanks + 1)),
      backgroundColor: (ctx) => {
        const { chart } = ctx;
        const scale = chart.scales.r;
        if (!scale || !Number.isFinite(scale.xCenter) || !Number.isFinite(scale.yCenter) || !(scale.drawingArea > 0)) return 'rgba(18,184,178,0.18)'; // before the first layout
        const g = chart.ctx.createRadialGradient(scale.xCenter, scale.yCenter, 0, scale.xCenter, scale.yCenter, scale.drawingArea);
        g.addColorStop(0, 'rgba(18,184,178,0.38)');
        g.addColorStop(1, 'rgba(18,184,178,0.08)');
        return g;
      },
      borderColor: '#12B8B2', borderWidth: 2, borderJoinStyle: 'round', fill: true,
      pointBackgroundColor: docSlices.map(d => d.color), pointBorderColor: '#142534', pointBorderWidth: 2,
      pointRadius: 6, pointHoverRadius: 8, pointHitRadius: 16,
    }],
  };
  const docOptions = {
    responsive: true,
    maintainAspectRatio: false,
    // "Con factura" opens Facturas filtered to those facturas, "Solo conduce" the conduces never invoiced, "Acuerdo" the
    // deals, "Sin documento" the list of those despachos (DespachosPage).
    onClick: (evt, elements) => { const to = elements.length && docSlices[elements[0].index]?.to; if (to) navigate(to()); },
    onHover: (evt, elements) => { evt.native.target.style.cursor = elements.length && docSlices[elements[0].index]?.to ? 'pointer' : 'default'; },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: {
        title: (items) => docSlices[items[0].dataIndex]?.label,
        label: (c) => { const d = docSlices[c.dataIndex]; return `${d.tanks.toLocaleString('es-DO')} tanques (${pct(d.tanks)}%) · ${d.despachos} despacho${d.despachos === 1 ? '' : 's'}`; },
      } },
    },
    scales: {
      r: {
        beginAtZero: true,
        angleLines: { color: 'rgba(157,179,194,0.14)' },
        grid: { color: 'rgba(157,179,194,0.12)' },
        pointLabels: { color: '#9DB3C2', padding: 10, font: { size: 12, family: 'IBM Plex Sans', weight: '500' } },
        ticks: { display: false },
      },
    },
  };

  const yearlyChart = !showCompany ? null : {
    labels: MONTH_LABELS,
    datasets: [
      {
        label: String(data.currentYear),
        data: data.monthlyComparison.map(m => m.currentYear),
        borderColor: '#12B8B2',
        backgroundColor: '#12B8B2',
        tension: 0.3,
      },
      {
        label: String(data.lastYear),
        data: data.monthlyComparison.map(m => m.lastYear),
        borderColor: '#4ADE80',
        backgroundColor: '#4ADE80',
        tension: 0.3,
      },
    ],
  };

  const hasYearlyData = showCompany && (data.hasCurrentYearData || data.hasLastYearData);

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-4" style={{ textAlign: 'left' }}>Panel de Ventas</h2>

      <Row className="mb-3">
        {showCompany ? (
          <>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Ventas" to="/invoices" navigate={navigate}>
                <PeriodRows values={data.periods.ventas} format={money} color="var(--mm-cyan)" rows={PERIOD_ROWS_YEAR} />
                <YoyComparison current={data.yoyComparison.ventas.current} previous={data.yoyComparison.ventas.previous} month={data.currentMonth} year={data.lastYear} format={money} />
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Ingresos recibidos" to="/ingresos?tab=history" navigate={navigate}>
                <PeriodRows values={data.periods.ingresos} format={money} color="var(--mm-green)" rows={PERIOD_ROWS_YEAR} />
                <YoyComparison current={data.yoyComparison.ingresos.current} previous={data.yoyComparison.ingresos.previous} month={data.currentMonth} year={data.lastYear} format={money} />
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Cuentas por cobrar" to="/reports?tab=cobros" navigate={navigate}>
                <h2 className="mt-2 mb-1" style={{ color: 'var(--mm-yellow)' }}>{money(data.periods.porCobrar.total)}</h2>
                <div className="small text-muted">Pendiente de lo facturado:</div>
                <PeriodRows values={data.periods.porCobrar} format={money} color="var(--mm-yellow)" />
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <ExpensesCard gastos={data.gastosMes} navigate={navigate} />
            </Col>
          </>
        ) : (
          <>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Ventas hoy" to="/invoices" navigate={navigate}>
                <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{money(data.ventasHoy)}</h2>
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Facturado por ti esta semana" to="/invoices" navigate={navigate}>
                <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{money(data.miFacturacionSemana?.total)}</h2>
                <div className="small text-muted mt-1">
                  {data.miFacturacionSemana?.count || 0} {data.miFacturacionSemana?.count === 1 ? 'factura' : 'facturas'} · de lunes a domingo
                </div>
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Ingresos recibidos hoy" to="/ingresos?tab=history" navigate={navigate}>
                <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-green)' }}>{money(data.ingresosHoy)}</h2>
                <div className="small text-muted mt-1">{data.cobrosHoy || 0} {data.cobrosHoy === 1 ? 'cobro verificado' : 'cobros verificados'} · todos los usuarios</div>
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Cotizaciones hoy" to="/quotes" navigate={navigate}>
                <h2 className="mt-2 mb-0">{data.cotizacionesHoy}</h2>
              </StatCard>
            </Col>
          </>
        )}
      </Row>

      <Row className="mb-3">
        <Col md={6} xl={showCompany ? 4 : 6} className={showCompany ? 'mb-3 mb-xl-0' : undefined}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Ventas vs Cotizaciones por Día (mes actual)</Card.Title>
            {showCompany
              ? <Bar data={dailyChart} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
              : fitToScreen(<Bar data={dailyChart} options={sideBySide} />)}
          </Card>
        </Col>
        {!showCompany && (
          <Col md={6}>
            <Card className="shadow-sm p-3 card-equal-height">
              <Card.Title>Recibos de ingresos por día (mes actual)</Card.Title>
              {fitToScreen(
                <Bar
                  data={incomeChart}
                  options={{ ...sideBySide, plugins: { ...sideBySide.plugins, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y)}` } } } }}
                />,
              )}
            </Card>
          </Col>
        )}
        {showCompany && (
        <Col md={6} xl={4} className="mb-3 mb-xl-0">
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Ventas por Mes — {data.currentYear} vs {data.lastYear}</Card.Title>
            {hasYearlyData ? (
              <Line data={yearlyChart} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
            ) : (
              <div className="text-center py-5">
                <i className="bi bi-graph-up-arrow pulse-glow" style={{ fontSize: '3rem', color: 'var(--mm-cyan)' }} />
                <div className="mt-3" style={{ color: 'var(--mm-muted)', fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.1rem' }}>
                  Todavía no hay suficiente historial para comparar años.
                </div>
                <div className="text-muted small mt-1">Esta gráfica despertará en cuanto empieces a facturar.</div>
              </div>
            )}
          </Card>
        </Col>
        )}
        {showCompany && (
        <Col md={12} xl={4}>
          <Card className="shadow-sm p-3 card-equal-height">
            <div className="d-flex justify-content-between align-items-baseline gap-2">
              <Card.Title>Despachos por documento (mes actual)</Card.Title>
              {byDocument && docTanks > 0 && <button type="button" className="btn btn-link btn-sm p-0" style={{ whiteSpace: 'nowrap' }} onClick={() => navigate(`/despachos?mes=${todayDR().slice(0, 7)}`)}>Ver despachos</button>}
            </div>
            {!byDocument ? (
              <div className="text-muted py-4">Manifold Manager no respondió; no se pueden mostrar los despachos.</div>
            ) : docTanks === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-box-seam" style={{ fontSize: '3rem', color: 'var(--mm-yellow)' }} />
                <div className="mt-3" style={{ color: 'var(--mm-muted)', fontFamily: HEADING_FONT, fontSize: '1.1rem' }}>Todavía no hay despachos este mes.</div>
              </div>
            ) : (
              <>
                <div style={{ height: 230 }}>
                  <Radar data={docChart} options={docOptions} />
                </div>
                <div className="doc-legend mt-2">
                  {docSlices.map(d => (
                    <button type="button" key={d.key} className="doc-legend-item" onClick={() => navigate(d.to())}>
                      <span className="doc-legend-dot" style={{ background: d.color }} />
                      <span className="doc-legend-label">{d.label}</span>
                      <span className="doc-legend-value">{d.tanks.toLocaleString('es-DO')}</span>
                      <span className="doc-legend-pct">{pct(d.tanks)}%</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </Card>
        </Col>
        )}
      </Row>

      {showCompany && (
      <Row>
        <Col md={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Top 5 Clientes ({data.currentYear})</Card.Title>
            {data.topClients.length === 0 ? (
              <div className="text-muted">Sin datos todavía.</div>
            ) : (
              <table className="table table-sm mb-0">
                <thead>
                  <tr><th>Cliente</th><th className="text-end">Total anual</th></tr>
                </thead>
                <tbody>
                  {data.topClients.map((c, i) => (
                    <tr key={i}>
                      <td>{c.name}</td>
                      <td className="text-end">{money(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>{paceFromManifold ? 'Tanques despachados' : 'Cilindros vendidos'}: este mes vs el mes pasado</Card.Title>
            {!pace ? (
              <div className="text-muted">Sin datos todavía.</div>
            ) : (
              <>
                <div className="d-flex align-items-baseline flex-wrap mb-2" style={{ gap: 12 }}>
                  <span style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--mm-cyan)' }}>{pace.total.toLocaleString('es-DO')}</span>
                  <span className="small text-muted">{paceFromManifold ? 'tanques despachados hasta hoy' : 'cilindros facturados hasta hoy'}</span>
                  {pace.lastMonthSameDay > 0 && (
                    <span className="small" style={{ color: pace.total >= pace.lastMonthSameDay ? 'var(--mm-green)' : 'var(--mm-red)' }}>
                      {pace.total >= pace.lastMonthSameDay ? '▲' : '▼'} {Math.abs(Math.round(((pace.total - pace.lastMonthSameDay) / pace.lastMonthSameDay) * 100))}% vs el mismo día del mes pasado ({pace.lastMonthSameDay.toLocaleString('es-DO')})
                    </span>
                  )}
                </div>
                <div style={{ height: 240 }}>
                  <Line data={paceChart} options={paceOptions} />
                </div>
                <div className="small text-muted mt-2">Acumulado día a día. El mes pasado cerró con {pace.lastMonthTotal.toLocaleString('es-DO')}.{paceFromManifold ? '' : ' Manifold Manager no respondió: son los cilindros facturados.'}</div>
              </>
            )}
          </Card>
        </Col>
      </Row>
      )}
    </div>
  );
};

export default SalesDashboard;
